<template>
    <CompassPanel
        v-if="displayPanel"
        class="panel-create-activity"
        :value="true"
        :title="panelTitle"
        :icon="panelIcon"
        @beforeClose="onClosePanel"
    >
        <template #default>
            <ActivityDetailPanelContentLoadingState v-if="isLoading" />
            <template v-else>
                <h3 class="panel-create-activity__content-title typographies-heading-h2">
                    {{ localizedTexts.contentTitle }}
                </h3>
                <div class="panel-create-activity__form">
                    <CompassFormItem
                        :label="$t.__('common.labels.title')"
                        orientation="horizontal"
                        size="small"
                    >
                        <CompassInput
                            ref="inputTitle"
                            v-model="activityFormData.title"
                            :placeholder="localizedTexts.placeholderActivityTitle"
                        />
                    </CompassFormItem>
                    <CompassDescriptionList type="alternative">
                        <CompassDescriptionListItem :label="localizedTexts.priority">
                            <ActivitiesPriorityIconButton
                                :isActive="activityIsPrioritized"
                                enabled
                                @clicked="togglePriority"
                            />
                        </CompassDescriptionListItem>
                    </CompassDescriptionList>
                    <CompassFormItem
                        :label="localizedTexts.label"
                        orientation="horizontal"
                        size="small"
                    >
                        <CompassSelect
                            v-model="activityFormData.label_id"
                            menuClass="select-labels-activity"
                        >
                            <CompassDropdownSelectOption
                                v-for="label in labelsOptions"
                                :key="label.id"
                                :label="label.label"
                                :dotColor="label.color"
                                :value="label.id"
                                type="dot"
                            />
                        </CompassSelect>
                    </CompassFormItem>

                    <component
                        :is="dateFormComponent"
                        v-if="dateFormComponent"
                        :activityDate="activityDate"
                        :workingDays="metas.working_days"
                        :workingHours="metas.working_hours"
                        :activityData="activityData"
                        @updateFormIsInvalid="(value) => (formIsInvalid = value)"
                        @update:activityDate="(value) => (activityDate = value)"
                    />

                    <CompassFormItem
                        :label="$t.__('common.labels.assignee')"
                        orientation="horizontal"
                        size="small"
                    >
                        <CompassSelect
                            v-model="activityFormData.owner_id"
                            :filterable="true"
                            menuClass="select-staffs-activity"
                        >
                            <CompassDropdownSelectOption
                                v-for="staff in availableOwner"
                                :key="staff.id"
                                :label="`${staff.firstname} ${staff.lastname}`"
                                :value="staff.id"
                                type="avatar"
                                :disabled="staff.disabled"
                                :avatar="
                                    getAvatarObject(staff.firstname, staff.lastname, staff.avatar)
                                "
                            />
                        </CompassSelect>
                    </CompassFormItem>
                    <CompassFormItem
                        :label="$t.__('common.labels.select_other_staff')"
                        orientation="horizontal"
                        size="small"
                    >
                        <CompassSelect
                            ref="selectAssignedStaff"
                            v-model="activityFormData.staffs"
                            :multiple="true"
                            :filterable="true"
                            :placeholder="$t.__('common.labels.search_several_staff')"
                            menuClass="select-staffs-activity"
                        >
                            <CompassDropdownSelectOption
                                v-for="staff in availableAssignedStaff"
                                :key="staff.id"
                                :label="`${staff.firstname} ${staff.lastname}`"
                                :value="staff.id"
                                type="avatar"
                                :disabled="staff.disabled"
                                :avatar="
                                    getAvatarObject(staff.firstname, staff.lastname, staff.avatar)
                                "
                            />
                        </CompassSelect>
                    </CompassFormItem>
                    <CompassFormItem
                        v-if="shouldDisplayContactInput"
                        :label="localizedTexts.contacts"
                        orientation="horizontal"
                        size="small"
                        class="contacts-input-label"
                    >
                        <ActivityFormPanelContactInput
                            :contacts="activityFormData.contacts"
                            :dataFromAutocomplete="dataFromAutocomplete"
                            :isDropdownLoading="isDropdownLoading"
                            :embedParticipants="embedParticipants"
                            @update:contacts="updateContacts"
                            @input="getDataFromAutocomplete"
                        />
                    </CompassFormItem>
                    <CompassFormItem
                        :label="localizedTexts.description"
                        orientation="vertical"
                        size="small"
                    >
                        <RichText
                            v-model="activityFormData.description"
                            :options="optionsRichText"
                            class="description-input-max-height"
                        />
                    </CompassFormItem>

                    <CompassAccordion
                        :title="$t.__('activities.activitiesFormPanel.linked_object')"
                        open
                        variant="h2"
                    >
                        <CompassButton
                            v-if="!activityFormData.related.length"
                            :label="
                                $t.__(
                                    'activities.dialog.ActivitiesLinkObjectDialog.link_object_title',
                                )
                            "
                            type="tertiary"
                            leftIcon="objects-linked"
                            @click="handleOpenLinkObjectDialog"
                        />
                        <LinkedObject
                            v-else
                            :object="activityFormData.related[0].object"
                            :name="activityFormData.related[0].name"
                            :link="activityFormData.related[0].link"
                            :isRemovable="true"
                            @remove="activityFormData.related = []"
                            @onClickLinkedObject="handleOpenLinkObjectDialog"
                        />
                    </CompassAccordion>

                    <CompassAccordion
                        :title="$t.__('common.labels.advanced_settings')"
                        variant="h2"
                        open
                        class="panel-create-activity__form-advanced-settings"
                    >
                        <CompassFormItem
                            :label="localizedTexts.privacy.title"
                            orientation="horizontal"
                            size="small"
                            inputSize="none"
                        >
                            <CompassRadioGroup v-model="privacy" label="" direction="horizontal">
                                <CompassRadio
                                    modelValue="public"
                                    :label="localizedTexts.privacy.public"
                                />
                                <CompassRadio
                                    modelValue="private"
                                    :label="localizedTexts.privacy.private"
                                />
                            </CompassRadioGroup>
                        </CompassFormItem>
                        <ActivityFormPanelReminder
                            v-if="activityType === ACTIVITY_TYPE.TASK"
                            :reminders="activityFormData.reminders"
                            :isEditMode="!!activityData"
                            displayReminderNotifInApp
                            @updateActivityReminder="
                                (reminders) => (activityFormData.reminders = reminders)
                            "
                        />
                    </CompassAccordion>
                </div>
            </template>
        </template>
        <template #footer>
            <div v-if="!isLoading" class="panel-create-activity__footer">
                <!-- RESCHEDULE -->
                <CompassButton
                    :label="localizedTexts.cancelBtn"
                    type="secondary"
                    :disabled="saveIsPending"
                    @click="onClosePanel"
                />

                <!-- STATUS -->
                <CompassButton
                    :label="localizedTexts.saveBtn"
                    :loading="saveIsPending"
                    :disabled="formIsInvalid"
                    @click="onSave"
                />
            </div>
        </template>
    </CompassPanel>
</template>

<script>
import ActivityDetailPanelContentLoadingState from 'vcomponents/activities/detailPanel/ActivityDetailPanelContentLoadingState.vue';
import ActivityFormPanelDateEvent from 'vcomponents/activities/activitiesFormPanel/ActivityFormPanelDateEvent.vue';
import ActivityFormPanelDateTask from 'vcomponents/activities/activitiesFormPanel/ActivityFormPanelDateTask.vue';
import ActivitiesPriorityIconButton from 'vcomponents/activities/priorityIconButton/ActivitiesPriorityIconButton.vue';
import ActivityFormPanelContactInput from 'vcomponents/activities/activitiesFormPanel/ActivityFormPanelContactInput.vue';
import ActivityFormPanelReminder from 'vcomponents/activities/activitiesFormPanel/ActivityFormPanelReminder.vue';
import LinkedObject from 'vcomponents/common/LinkedObject/LinkedObject.vue';
import { fetchActivitiesCrmMetas } from 'api/activities/activitiesService.js';
import ActivityFormService from 'api/activities/activityFormService.js';
import { getContacts, searchContacts } from 'api/contacts/contactsService.js';
import ActivityFormMixins from 'mixins/activities/ActivityFormMixins.js';
import { getAvatarObject } from 'utils/avatar';
import { createNamespacedHelpers } from 'vuex';
import RichText from 'vcomponents/richtext/RichText.vue';
import { capitalize, cloneDeep } from 'lodash';
import orderStaffListByOwnerAndName from 'Helpers/orderStaffListByOwnerAndName';
import { adaptRelatedForActivity } from 'Helpers/activities/relatedAdapter.js';
import * as Sentry from '@sentry/vue';

const popupPanelMapActions = createNamespacedHelpers('popupPanel').mapActions;
const userMapState = createNamespacedHelpers('user').mapState;
const ACTIVITY_TYPE = Object.freeze({
    TASK: 'task',
    EVENT: 'event',
});

export default {
    components: {
        ActivityFormPanelReminder,
        RichText,
        ActivitiesPriorityIconButton,
        ActivityFormPanelDateEvent,
        ActivityDetailPanelContentLoadingState,
        ActivityFormPanelDateTask,
        ActivityFormPanelContactInput,
        LinkedObject,
    },
    mixins: [ActivityFormMixins],
    props: {
        activityType: {
            type: String,
            required: true,
        },
        id: {
            type: Number,
            default: null,
        },
        related: {
            type: Object,
            default: null,
        },
        callback: {
            type: Function,
            default: null,
        },
    },
    data() {
        return {
            ACTIVITY_TYPE,
            formIsInvalid: false,
            isLoading: true,
            displayPanel: false,
            isDropdownLoading: false,
            labelsOptions: [],
            metas: {},
            activityDate: null,
            activityFormData: {
                assigned_staff_ids: [],
                label_id: null,
                title: '',
                description: '',
                related: this.related ? [this.related] : [],
                owner_id: null,
                staffs: [],
                is_private: false,
                contacts: [],
                priority: 0,
                reminders: [],
            },
            saveIsPending: false,
            activityData: null,
            activityFormService: null,
            dataFromAutocomplete: [],
            controller: new AbortController(),
            privacy: 'public',
            optionsRichText: [
                'Bold',
                'Italic',
                'Underline',
                'TextAlign',
                'BulletList',
                'OrderedList',
                'Divider',
                'Link',
            ],
            availableAssignedStaff: [],
            availableOwner: [],
        };
    },
    computed: {
        ...userMapState({ idCurrentUser: 'id', agendaProvider: 'agendaProvider' }),
        panelTitle() {
            if (this.isLoading) {
                return '';
            }
            switch (this.activityType) {
                case 'task':
                    return this.$l10n.__('Tâche');
                case 'phonecall':
                    return this.$l10n.__('Appel');
                case 'event':
                    return this.$l10n.__('Événement');
                default:
                    return '';
            }
        },
        panelIcon() {
            if (this.isLoading) {
                return null;
            }
            switch (this.activityType) {
                case 'task':
                    return 'obj-slsy-task';
                case 'event':
                    return 'obj-slsy-calendar';
                case 'phonecall':
                    return 'obj-slsy-phone';
                default:
                    return null;
            }
        },
        embedParticipants() {
            return this.activityData?._embed?.participants ?? [];
        },
        staffs() {
            return this.metas.authors.filter(
                (staff) => staff.id !== this.idCurrentUser && staff.status === 'ok',
            );
        },
        dateFormComponent() {
            if (this.activityType === ACTIVITY_TYPE.TASK) {
                return ActivityFormPanelDateTask;
            } else if (this.activityType === ACTIVITY_TYPE.EVENT) {
                return ActivityFormPanelDateEvent;
            }
            return null;
        },
        isAgendaProviderNotSellsy() {
            return this.agendaProvider !== 'sellsy';
        },
        shouldDisplayContactInput() {
            return this.isAgendaProviderNotSellsy && this.activityType === ACTIVITY_TYPE.EVENT;
        },
    },
    watch: {
        activityType() {
            if (!this.id) {
                this.initPanel();
            }
        },
        $route() {
            this.onClosePanel();
        },
    },
    async mounted() {
        await this.initPanel();
        this.$refs.selectAssignedStaff?.resizeInputHeight();
    },
    methods: {
        ...popupPanelMapActions(['close', 'open']),
        getAvatarObject,
        async initPanel() {
            try {
                this.displayPanel = true;
                this.isLoading = true;
                this.activityFormService = new ActivityFormService(this.activityType);
                this.metas = this.$store.getters['activitiesStores/getMetas'];
                this.activityFormData.owner_id = this.idCurrentUser;
                this.activityFormData.staffs = [];

                //We want to mock loading wait
                if (!this.id && this.metas) {
                    await new Promise((resolve) => setTimeout(resolve, 1000));
                }

                if (!this.metas) {
                    const { data: metas } = await fetchActivitiesCrmMetas();
                    this.metas = metas;
                }

                this.labelsOptions =
                    this.activityType === 'task' ? this.metas.task.labels : this.metas.event.labels;

                if (this.id) {
                    let data = this.$store.getters['activitiesStores/getActivityDataPanel'];
                    if (!data) {
                        data = await this.activityFormService.getActivity(this.id);
                    }
                    this.activityData = data;
                    this.privacy = data.is_private ? 'private' : 'public';
                    let staffs = data.staffs ?? data.assigned_staff_ids ?? [];
                    if (this.activityType === ACTIVITY_TYPE.EVENT) {
                        staffs = data.participants
                            .filter((participant) => participant.type === 'staff')
                            .map((participant) => participant.id);
                    } else if (this.activityType === ACTIVITY_TYPE.TASK) {
                        staffs = orderStaffListByOwnerAndName(data._embed.assigned_staffs)
                            .filter((staff) => staff.id !== data.owner.id)
                            .map((staff) => staff.id);
                    }
                    const reminders =
                        data.reminders?.map(({ medium, delay }) => ({ medium, delay })) ?? [];

                    this.activityFormData = {
                        label_id: data.label?.id,
                        title: data.title ?? '',
                        description: data.description ?? '',
                        related: [],
                        owner_id: data.owner.id,
                        id: this.id,
                        staffs: staffs,
                        contacts:
                            data.participants?.filter(
                                (participant) => participant.type !== 'staff',
                            ) ?? [],
                        priority: data.priority,
                        reminders,
                    };
                    if (!!data.related.length) {
                        this.activityFormData.related = [
                            adaptRelatedForActivity({
                                ...cloneDeep(data._embed.related[0]),
                                ...cloneDeep(data.related[0]),
                                type: data.related[0]?.type,
                                subtype: data._embed.related[0]?.type,
                            }),
                        ];
                    }
                } else if (!!this.activityFormData.related.length) {
                    let related = {};
                    const activityDataPanel =
                        this.$store.getters['activitiesStores/getActivityDataPanel'];

                    // create a new activity from another activity
                    if (!!activityDataPanel?._embed?.related?.length) {
                        related = activityDataPanel?._embed?.related[0];
                        this.activityFormData.related = [
                            adaptRelatedForActivity({
                                ...related,
                                ...cloneDeep(this.activityFormData.related[0]),
                                type: activityDataPanel.related[0]?.type,
                                subtype: related?.type,
                            }),
                        ];
                    } else {
                        //search data from overview stores
                        switch (this.activityFormData.related[0].type) {
                            case 'company':
                            case 'individual':
                                related = this.$store.state.third.dataFetch.identity;
                                break;
                            case 'contact':
                                related = this.$store.state.people.dataFetch.identity;
                                break;
                            case 'opportunity':
                                if (
                                    +(
                                        this.$store.state.opportunity?.dataFetch?.identity?.id ?? 0
                                    ) === this.activityFormData.related[0].id
                                ) {
                                    related = this.$store.state.opportunity.dataFetch.identity;
                                    related.status =
                                        this.$store.state.opportunity.dataFetch.informations.find(
                                            ({ key }) => key === 'signed',
                                        ).value;
                                } else {
                                    const data = await ApiClient.get(
                                        `/opportunities/${this.activityFormData.related[0].id}`,
                                    );
                                    related = data.data;
                                }

                                break;
                            default:
                                related = this.$store.state.documentOverview;
                        }
                        this.activityFormData.related = [
                            adaptRelatedForActivity({
                                ...related,
                                ...cloneDeep(this.activityFormData.related[0]),
                                subtype: related?.type,
                            }),
                        ];
                    }
                }

                if (
                    !this.activityFormData.label_id ||
                    !this.labelsOptions.some(({ id }) => id === this.activityFormData.label_id)
                ) {
                    this.activityFormData.label_id = +this.labelsOptions[0]?.id ?? null;
                }

                if (this.activityType === ACTIVITY_TYPE.EVENT) {
                    await this.getDataFromAutocomplete();
                }

                // include disabled staff if already in the selected list
                this.availableAssignedStaff = this.metas.authors.filter(
                    (staff) =>
                        staff.status === 'ok' ||
                        this.activityData?.assigned_staff_ids.includes(staff.id),
                );

                this.availableOwner = this.metas.authors.filter(
                    (staff) => staff.status === 'ok' || this.activityData?.owner?.id === staff.id,
                );
            } catch (e) {
                Sentry.captureException(e);
                console.error(e);
                this.$pushToast({
                    type: 'error',
                    text: this.$l10n.__(
                        'Une erreur est survenue lors de la récupération des données',
                    ),
                    icon: true,
                });
                this.onClosePanel();
            } finally {
                this.isLoading = false;

                setTimeout(() => {
                    this.$refs.inputTitle?.setFocus();
                }, 0);
            }
        },
        onClosePanel() {
            if (this.$store.state.activitiesStores) {
                this.$store.dispatch('activitiesStores/updateActivityDataPanel', null);
                this.$store.dispatch('activitiesStores/updateActivitySelected', null);
            }
            this.$emit('close');
        },
        async onSave() {
            try {
                this.saveIsPending = true;

                const title =
                    this.activityFormData.title ||
                    this.labelsOptions.find(({ id }) => id === this.activityFormData.label_id)
                        .label;
                const activity = {
                    ...this.activityFormData,

                    is_private: this.privacy === 'private',
                    title,
                };
                if (!!this.activityFormData.related.length) {
                    activity.related = [
                        {
                            id: this.activityFormData.related[0]?.id,
                            type: this.activityFormData.related[0]?.type,
                        },
                    ];
                }
                const promise = this.id
                    ? this.activityFormService.updateActivity({
                          activity,
                          date: this.activityDate,
                      })
                    : this.activityFormService.createActivity({
                          activity,
                          date: this.activityDate,
                      });
                const { data: data } = await promise;

                this.$pushToast({
                    type: 'success',
                    text: this.id
                        ? this.$l10n.__("L'activité a bien été mise à jour")
                        : this.$l10n.__('L’activité a bien été créée'),
                    icon: true,
                });
                if (this.callback) {
                    this.callback(data);
                    this.onClosePanel();
                    return;
                }
                this.refreshDashboardActivitiesCardIfNeeded(data?.due_date);
                if (this.activityType === ACTIVITY_TYPE.TASK) {
                    Agenda.updateListingTasks();
                }
                this.onClosePanel();
            } catch (e) {
                Sentry.captureException(e);
                let text = this.id
                    ? this.$t.__('activities.labels.error_activity_update')
                    : this.$t.__('activities.labels.error_activity_create');
                if (e?.response?.status === 403) {
                    text = this.$t.__('common.labels.no_permissions_to_perform_this_action');
                }

                this.$pushToast({
                    type: 'error',
                    text,
                    icon: true,
                });
                this.saveIsPending = false;
            }
        },
        refreshDashboardActivitiesCardIfNeeded(dueDate) {
            const isDashboardCardEnabled = document.querySelector('.dashboard__my-activities-card');
            const maxDueDateForRefresh = moment('23:59:59', 'HH:mm:ss').add(7, 'days');
            if (isDashboardCardEnabled && moment(dueDate).isBefore(maxDueDateForRefresh)) {
                EventBus.emit('dashboard-activities-card:refreshActivities');
            }
        },
        updateContacts(contact) {
            this.activityFormData.contacts = contact;
        },
        async searchContacts(query, queryLimit) {
            try {
                const {
                    data: { data: contacts },
                } = await searchContacts(query, ['contact'], queryLimit, this.controller.signal);

                return contacts.map((contact) => {
                    return {
                        first_name: capitalize(contact.first_name),
                        last_name: capitalize(contact.last_name),
                        email: contact.email,
                        id: contact?.object?.id,
                        type: contact?.object?.type,
                        avatar: contact?.object?.avatar,
                    };
                });
            } catch (error) {
                if (error.name === 'AbortError') return [];
                Sentry.captureException(error);
                this.$pushToast({
                    type: 'error',
                    icon: true,
                    text: this.$l10n.__(
                        'Un problème est survenu lors de la recherche. Veuillez réessayer plus tard.',
                    ),
                    close: true,
                });
            }
        },
        async getDataFromAutocomplete(input) {
            const queryLimit = 5;
            this.isDropdownLoading = true;
            if (input) {
                this.controller.abort();
                this.controller = new AbortController();
                this.isDropdownLoading = true;
                this.dataFromAutocomplete = await this.searchContacts(input, queryLimit);
                this.dataFromAutocomplete = this.dataFromAutocomplete.filter(
                    (item) => item.type === 'contact',
                );
            } else {
                const {
                    data: { data: data },
                } = await getContacts(queryLimit);
                this.dataFromAutocomplete = data;
            }
            this.isDropdownLoading = false;
        },
        handleOpenLinkObjectDialog() {
            let related = null;
            if (!!this.activityFormData.related.length) {
                related = {
                    ...this.activityData?._embed?.related[0],
                    ...this.activityFormData?.related[0],
                };
            }
            this.open({
                key: 'activitiesLinkObjectDialog',
                props: {
                    related,
                    callback: (objectSelected) => {
                        if (this.activityData?._embed?.related?.at(0)) {
                            this.activityData._embed.related[0] = objectSelected;
                        }
                        this.activityFormData.related = [objectSelected];
                    },
                },
            });
        },
    },
};
</script>

<style lang="scss">
.panel-create-activity {
    .contacts-input-label {
        display: flex;
        align-items: baseline;
    }

    .description-input-max-height {
        .ProseMirror {
            height: 260px;
            max-height: 260px;
        }
    }

    &__content-title {
        margin: 0 0 var(--spacing-1);
    }

    &__form {
        display: flex;
        flex-direction: column;
        gap: var(--spacing-4);
        &-advanced-settings > .compass-accordion__content {
            display: flex;
            flex-direction: column;
            gap: var(--spacing-3);
        }
    }

    &__footer {
        display: flex;
        justify-content: end;
        padding: var(--spacing-3) var(--spacing-5);
        gap: var(--spacing-3);
        border-top: 1px solid var(--colors-common-border-default);
        background: var(--colors-common-background-default);
    }
}
</style>
<style lang="scss">
#compass-dropdown-container {
    .select-labels-activity,
    .select-staffs-activity,
    .select-time-activity,
    .select-menu-reminder-activity {
        z-index: 1001;
    }

    .select-time-activity,
    .select-menu-reminder-activity {
        width: fit-content;
    }
}
#compass-date-picker-container {
    .panel-create-activity__datepicker {
        z-index: 1001;
    }
}

#compass-popover-container {
    .panel-create-activity__datepicker {
        z-index: 1001 !important;
    }
}
</style>
